import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import logo from '../../assets/images/logo.png';
import signature from '../../assets/images/signature.png';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    backgroundColor: '#ffffff'
  },
  header: {
    marginBottom: 30,
    borderBottom: '1px solid #D4AF37',
    paddingBottom: 15,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12
  },
  logoImg: {
    width: 60,
    height: 60
  },
  empresaContainer: {
    flexDirection: 'column'
  },
  empresaNombre: {
    fontSize: 16,
    color: '#D4AF37',
    fontWeight: 'bold'
  },
  empresaSlogan: {
    fontSize: 9,
    color: '#080808',
    fontStyle: 'italic',
    marginTop: 2
  },
  headerRight: {
    textAlign: 'right'
  },
  headerRightText: {
    fontSize: 8,
    color: '#666'
  },
  titulo: {
    fontSize: 18,
    color: '#D4AF37',
    marginBottom: 25,
    textAlign: 'center',
    fontWeight: 'bold',
    marginTop: 10
  },
  seccion: {
    marginBottom: 18,
    marginTop: 8
  },
  seccionTitulo: {
    fontSize: 12,
    backgroundColor: '#f5f5f5',
    padding: 6,
    marginBottom: 10,
    fontWeight: 'bold',
    color: '#080808',
    borderLeft: '3px solid #D4AF37'
  },
  fila: {
    flexDirection: 'row',
    marginBottom: 6,
    borderBottom: '1px solid #f0f0f0',
    paddingBottom: 4
  },
  label: {
    width: '35%',
    fontSize: 9,
    color: '#666',
    fontWeight: 'bold'
  },
  valor: {
    width: '65%',
    fontSize: 9,
    color: '#080808'
  },
  checklistItem: {
    flexDirection: 'row',
    marginBottom: 6,
    alignItems: 'center',
    paddingVertical: 2
  },
  checklistIcon: {
    width: 18,
    fontSize: 10,
    marginRight: 6,
    color: '#D4AF37'
  },
  checklistText: {
    fontSize: 9,
    color: '#080808',
    flex: 1
  },
  progresoBar: {
    marginVertical: 8,
    backgroundColor: '#f0f0f0',
    height: 6,
    borderRadius: 3,
    overflow: 'hidden'
  },
  progresoFill: {
    height: 6,
    backgroundColor: '#D4AF37',
    borderRadius: 3
  },
  progresoText: {
    fontSize: 8,
    color: '#666',
    textAlign: 'right',
    marginTop: 4
  },
  firmaContainer: {
    marginTop: 40,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%'
  },
  firma: {
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%'
  },
  firmaImagen: {
    width: 150,
    height: 45,
    marginBottom: -8,
    paddingBottom: 0,
    objectFit: 'contain'
  },
  lineaFirma: {
    borderTop: '1px solid #080808',
    width: '40%',
    marginTop: -3,
    marginBottom: 5,
    alignSelf: 'center'
  },
  firmaTexto: {
    fontSize: 9,
    color: '#080808',
    textAlign: 'center',
    marginTop: 5
  },
  firmaSubtitulo: {
    fontSize: 8,
    color: '#666',
    textAlign: 'center',
    marginTop: 2
  },
  selloContainer: {
    marginTop: 30,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%'
  },
  lineaSello: {
    borderTop: '1px solid #080808',
    width: '60%',
    marginTop: 0,
    marginBottom: 5,
    alignSelf: 'center'
  },
  selloTexto: {
    fontSize: 9,
    color: '#080808',
    textAlign: 'center',
    marginTop: 5
  },
  selloSubtitulo: {
    fontSize: 8,
    color: '#666',
    textAlign: 'center',
    marginTop: 2
  },
  nota: {
    fontSize: 7,
    color: '#999',
    marginTop: 30,
    fontStyle: 'italic',
    textAlign: 'center',
    paddingHorizontal: 20
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 7,
    color: '#999',
    borderTop: '1px solid #eeeeee',
    paddingTop: 8
  }
});

const InformePDF = ({ caso, cliente, verificacion }) => {
  const getChecklistItems = () => {
    const items = [];
    
    if (verificacion?.runt) {
      items.push({
        nombre: 'Verificacion RUNT',
        completado: verificacion.runt.verificada || false,
        detalle: verificacion.runt.resultado || (verificacion.runt.verificada ? 'Verificado correctamente' : 'Pendiente')
      });
    }
    
    if (verificacion?.simit) {
      items.push({
        nombre: 'Consulta SIMIT',
        completado: verificacion.simit.verificada || false,
        detalle: verificacion.simit.multas ? 'Tiene multas pendientes' : (verificacion.simit.verificada ? 'Sin multas registradas' : 'Pendiente')
      });
    }
    
    if (verificacion?.embargos) {
      items.push({
        nombre: 'Consulta Embargos',
        completado: verificacion.embargos.verificada || false,
        detalle: verificacion.embargos.tieneEmbargos ? 'Tiene embargos registrados' : (verificacion.embargos.verificada ? 'Sin embargos' : 'Pendiente')
      });
    }
    
    if (verificacion?.improntas) {
      items.push({
        nombre: 'Validacion Improntas',
        completado: verificacion.improntas.verificada || false,
        detalle: verificacion.improntas.coinciden ? 'Coinciden correctamente' : 'No coinciden'
      });
    }
    
    if (verificacion?.soat) {
      items.push({
        nombre: 'Verificacion SOAT',
        completado: verificacion.soat.verificada || false,
        detalle: verificacion.soat.vigente ? 'Vigente' : 'Vencido'
      });
    }
    
    if (verificacion?.tecnomecanica) {
      items.push({
        nombre: 'Verificacion TecnoMecanica',
        completado: verificacion.tecnomecanica.verificada || false,
        detalle: verificacion.tecnomecanica.vigente ? 'Vigente' : 'Vencido'
      });
    }
    
    return items;
  };

  const calcularPorcentaje = () => {
    const items = getChecklistItems();
    if (items.length === 0) return 0;
    const completados = items.filter(i => i.completado).length;
    return Math.round((completados / items.length) * 100);
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'No registrada';
    if (fecha.toDate) return fecha.toDate().toLocaleDateString('es-CO');
    if (fecha instanceof Date) return fecha.toLocaleDateString('es-CO');
    return new Date(fecha).toLocaleDateString('es-CO');
  };

  const checklistItems = getChecklistItems();
  const porcentaje = calcularPorcentaje();
  const fechaActual = new Date().toLocaleDateString('es-CO');

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {}
        <View style={styles.header}>
          <View style={styles.logoContainer}>
            <Image src={logo} style={styles.logoImg} />
            <View style={styles.empresaContainer}>
              <Text style={styles.empresaNombre}>Golden Cars</Text>
              <Text style={styles.empresaSlogan}>Mas que un peritaje, tu respaldo al comprar</Text>
            </View>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.headerRightText}>Golden Cars – Peritaje & Respaldo Juridico S.A.S.</Text>
          </View>
        </View>

        {}
        <Text style={styles.titulo}>INFORME DE VERIFICACION LEGAL</Text>

        {}
        <View style={styles.seccion}>
          <Text style={styles.seccionTitulo}>INFORMACION DEL CLIENTE</Text>
          <View style={styles.fila}>
            <Text style={styles.label}>Nombre Completo:</Text>
            <Text style={styles.valor}>{cliente?.nombre || 'No registrado'}</Text>
          </View>
          <View style={styles.fila}>
            <Text style={styles.label}>Documento:</Text>
            <Text style={styles.valor}>{cliente?.tipoDocumento?.toUpperCase()} {cliente?.documento || 'No registrado'}</Text>
          </View>
          <View style={styles.fila}>
            <Text style={styles.label}>Correo Electronico:</Text>
            <Text style={styles.valor}>{cliente?.email || 'No registrado'}</Text>
          </View>
          <View style={styles.fila}>
            <Text style={styles.label}>Telefono:</Text>
            <Text style={styles.valor}>{cliente?.telefono || 'No registrado'}</Text>
          </View>
          {cliente?.direccion && (
            <View style={styles.fila}>
              <Text style={styles.label}>Direccion:</Text>
              <Text style={styles.valor}>{cliente.direccion}</Text>
            </View>
          )}
        </View>

        {}
        <View style={styles.seccion}>
          <Text style={styles.seccionTitulo}>INFORMACION DEL VEHICULO</Text>
          <View style={styles.fila}>
            <Text style={styles.label}>Marca:</Text>
            <Text style={styles.valor}>{caso?.vehiculo?.marca || 'No especificada'}</Text>
          </View>
          <View style={styles.fila}>
            <Text style={styles.label}>Modelo:</Text>
            <Text style={styles.valor}>{caso?.vehiculo?.modelo || 'No especificado'}</Text>
          </View>
          <View style={styles.fila}>
            <Text style={styles.label}>Placa:</Text>
            <Text style={styles.valor}>{caso?.vehiculo?.placa?.toUpperCase() || 'No especificada'}</Text>
          </View>
          <View style={styles.fila}>
            <Text style={styles.label}>Anio:</Text>
            <Text style={styles.valor}>{caso?.vehiculo?.año || 'No especificado'}</Text>
          </View>
          {caso?.vehiculo?.precio && (
            <View style={styles.fila}>
              <Text style={styles.label}>Valor Comercial:</Text>
              <Text style={styles.valor}>${Number(caso.vehiculo.precio).toLocaleString('es-CO')}</Text>
            </View>
          )}
        </View>

        {}
        <View style={styles.seccion}>
          <Text style={styles.seccionTitulo}>INFORMACION DEL SERVICIO</Text>
          <View style={styles.fila}>
            <Text style={styles.label}>Tipo de Servicio:</Text>
            <Text style={styles.valor}>
              {caso?.tipoServicio === 'compra' ? 'Asesoria en Compra' :
               caso?.tipoServicio === 'venta' ? 'Asesoria en Venta' :
               caso?.tipoServicio === 'verificacion' ? 'Verificacion Legal Integral' : 
               caso?.tipoServicio === 'tramite' ? 'Tramite de Traspaso' : 'No especificado'}
            </Text>
          </View>
          <View style={styles.fila}>
            <Text style={styles.label}>Fecha de Inicio:</Text>
            <Text style={styles.valor}>{caso?.fechaCreacion ? formatearFecha(caso.fechaCreacion) : 'No registrada'}</Text>
          </View>
          {caso?.honorarios && (
            <View style={styles.fila}>
              <Text style={styles.label}>Honorarios Acordados:</Text>
              <Text style={styles.valor}>${Number(caso.honorarios).toLocaleString('es-CO')}</Text>
            </View>
          )}
        </View>

        {}
        <View style={styles.seccion}>
          <Text style={styles.seccionTitulo}>CHECKLIST DE VERIFICACIONES</Text>
          <View style={styles.progresoBar}>
            <View style={[styles.progresoFill, { width: `${porcentaje}%` }]} />
          </View>
          <Text style={styles.progresoText}>Progreso: {porcentaje}% completado</Text>
          {checklistItems.map((item, index) => (
            <View key={index} style={styles.checklistItem}>
              <Text style={styles.checklistIcon}>•</Text>
              <Text style={styles.checklistText}>{item.nombre}: {item.detalle}</Text>
            </View>
          ))}
        </View>

        {}
        <View style={styles.firmaContainer}>
          <View style={styles.firma}>
            <Image src={signature} style={styles.firmaImagen} />
            <View style={styles.lineaFirma} />
            <Text style={styles.firmaTexto}>Firma Abogado</Text>
            <Text style={styles.firmaSubtitulo}>Golden Cars</Text>
          </View>
        </View>
        {}
        <Text style={styles.nota}>
          Este informe ha sido elaborado con base en la informacion suministrada y las verificaciones realizadas.
          Este documento tiene caracter informativo y no constituye una garantia absoluta sobre el estado legal del vehiculo.
          Vigencia: 30 dias a partir de la fecha de emision.
        </Text>

        {}
        <Text style={styles.footer}>
          Golden Cars – Peritaje & Respaldo Juridico S.A.S. | Generado el {fechaActual}
        </Text>
      </Page>
    </Document>
  );
};

export default InformePDF;